import {CounterLabel} from '@primer/react'
import {Children, useState, useCallback, type ReactNode} from 'react'
import {ActionItemsList} from './ActionItemsList'
import styles from './CollapsibleActionItems.module.css'

interface CollapsibleActionItemsProps {
  items?: ReactNode[]
  children?: ReactNode
  /**
   * Number of items to show when collapsed. The toggle button effectively
   * occupies an additional slot, so a rollup only appears when the total
   * number of items exceeds `collapsedCount + 1`. Defaults to 2.
   */
  collapsedCount?: number
}

/**
 * Renders a list of action items with a "Show more / Show less" toggle when
 * the list exceeds `collapsedCount + 1` items. When collapsed, a single peek
 * card is rendered behind the toggle to hint at the hidden items; expand /
 * collapse animates the height of the extra-items region so the action list
 * appears to roll open and roll back up.
 */
export function CollapsibleActionItems({items, children, collapsedCount = 2}: CollapsibleActionItemsProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const toggle = useCallback(() => setIsExpanded(prev => !prev), [])

  const [extrasHeight, setExtrasHeight] = useState(0)

  // Callback ref re-runs whenever the collapsible node mounts or unmounts, so
  // the observer is attached even if the region appears later (e.g. items
  // crossing the rollup threshold after initial render). React 19 invokes the
  // returned function as cleanup when the node changes or unmounts.
  //
  // The ResizeObserver writes are coalesced into a single rAF and bail when the
  // height is unchanged. Writing state synchronously on every RO callback would
  // re-render, re-fire the observer, and restart the max-height transition —
  // each restart forces Safari to re-rasterize the animating layer.
  const extrasRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    let rafId = 0
    let lastHeight = -1
    const measure = () => {
      rafId = 0
      const next = node.scrollHeight
      if (next === lastHeight) return
      lastHeight = next
      setExtrasHeight(next)
    }
    const schedule = () => {
      if (rafId) return
      rafId = requestAnimationFrame(measure)
    }
    schedule()
    const ro = new ResizeObserver(schedule)
    ro.observe(node)
    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      ro.disconnect()
    }
  }, [])

  const allItems = items ?? Children.toArray(children)

  // The toggle occupies an additional visual slot, so only roll up when there
  // are more items than would fit alongside the toggle.
  if (allItems.length <= collapsedCount + 1) {
    return <ActionItemsList>{allItems}</ActionItemsList>
  }

  const hiddenCount = allItems.length - collapsedCount
  const visibleItems = allItems.slice(0, collapsedCount)
  const extraItems = allItems.slice(collapsedCount)

  return (
    <div className={styles.container}>
      <ActionItemsList>{visibleItems}</ActionItemsList>
      <div
        className={styles.collapsible}
        style={{maxHeight: isExpanded ? `${extrasHeight}px` : '0px'}}
        // `inert` removes the region from the focus order and accessibility
        // tree when collapsed without producing aria-hidden focusable-content
        // violations.
        inert={!isExpanded ? true : undefined}
      >
        <div ref={extrasRefCallback}>
          <ActionItemsList className={styles.extrasList}>{extraItems}</ActionItemsList>
        </div>
      </div>
      {isExpanded ? (
        <button type="button" className={styles.toggle} onClick={toggle} aria-expanded="true">
          Show less
        </button>
      ) : (
        <div className={styles.showMoreWrapper}>
          <button type="button" className={styles.toggle} onClick={toggle} aria-expanded="false">
            <span>Show more</span>
            <CounterLabel>{hiddenCount}</CounterLabel>
          </button>
        </div>
      )}
    </div>
  )
}
